import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpen, Video, FileText, StickyNote, Plus, Trash2, Edit2, ExternalLink,
  ChevronDown, ChevronRight, CheckCircle2, Circle, Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import LessonPicker, { getLessonsForSport } from './LessonPicker';
import { LessonContentEditor } from './LessonContentEditor';
import { LessonProgressPanel } from './LessonProgressPanel';
import { CoachLessonBuilder } from './CoachLessonBuilder';
import { AddContentModal } from './AddContentModal';
import { useAssignmentCompletion } from '@/hooks/useAssignmentCompletion';

import { useCoachLessons, isCustomLessonId, customLessonId } from '@/hooks/useCoachLessons';
import type { TeamMember } from '@/hooks/useCoach';
import { COACH_TIER_LIMITS } from '@/lib/stripeConfig';

type Tab = 'all' | 'lessons' | 'media';

type Assignment = {
  id: string;
  team_id: string;
  lesson_id: string;
  assigned_at: string;
};

type ContentItem = {
  id: string;
  type: 'video' | 'article' | 'note';
  title: string;
  url: string | null;
  description: string | null;
  sport: string;
  assigned_to: string[] | null;
  team_id: string | null;
  created_at: string;
};

type Viewer = { user_id: string; username: string | null; viewed_at?: string };
type ViewStats = { assigned_count: number; viewed_count: number; viewers: Viewer[]; pending: Viewer[] };
type AssignmentUsage = { active_assignments: number; active_unique_lessons: number; limit: number; tier: 'basic' | 'pro' };

type Row =
  | { kind: 'lesson'; id: string; title: string; sport: string; date: string; assignment: Assignment }
  | { kind: 'content'; id: string; title: string; sport: string; date: string; item: ContentItem };

const barColor = (pct: number) =>
  pct >= 75 ? 'bg-emerald-400' : pct >= 25 ? 'bg-amber-400' : 'bg-rose-400';

const TYPE_ICON = { video: Video, article: FileText, note: StickyNote } as const;
const TYPE_COLOR = { video: 'text-red-400', article: 'text-emerald-400', note: 'text-yellow-400' } as const;

type Props = {
  coachId: string;
  teamId: string;
  sport: string;
  members: TeamMember[];
  coachTier?: 'free' | 'pro';
  onUpgrade?: () => void;
};

export function ContentHub({ coachId, teamId, sport, members, coachTier = 'free', onUpgrade }: Props) {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('all');
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [content, setContent] = useState<ContentItem[]>([]);
  const [stats, setStats] = useState<Record<string, ViewStats>>({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ContentItem | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedLessons, setSelectedLessons] = useState<string[]>([]);
  const [assignmentUsage, setAssignmentUsage] = useState<AssignmentUsage>({
    active_assignments: 0,
    active_unique_lessons: 0,
    limit: COACH_TIER_LIMITS.free.assignments,
    tier: 'basic',
  });

  const { allLessons } = getLessonsForSport(sport);
  const { lessons: coachLessons } = useCoachLessons(coachId);

  const rosterPlayers = useMemo(
    () => members.map(m => ({ user_id: (m as any).player_user_id ?? (m as any).user_id, username: m.username ?? null })),
    [members],
  );

  const lessonTitle = useCallback((lessonId: string) => {
    if (isCustomLessonId(lessonId)) {
      return coachLessons.find(l => customLessonId(l.id) === lessonId)?.title ?? 'My lesson';
    }
    return allLessons.find(l => l.id === lessonId)?.title ?? lessonId;
  }, [allLessons, coachLessons]);

  const fetchAll = useCallback(async () => {
    const [a, c, usage] = await Promise.all([
      supabase.from('lesson_assignments').select('*').eq('team_id', teamId).order('assigned_at', { ascending: false }),
      supabase
        .from('coach_content' as any)
        .select('*')
        .eq('coach_id', coachId)
        .or(`team_id.eq.${teamId},team_id.is.null`)
        .order('created_at', { ascending: false }),
      supabase.rpc('get_coach_assignment_usage' as any),
    ]);
    const items = (c.data as any as ContentItem[]) ?? [];
    setAssignments((a.data as Assignment[]) ?? []);
    setContent(items);
    if (usage.data) setAssignmentUsage(usage.data as unknown as AssignmentUsage);
    setLoading(false);

    const results = await Promise.all(
      items.map(async (i) => {
        const { data } = await supabase.rpc('get_content_view_stats' as any, { p_content_id: i.id });
        return [i.id, data as unknown as ViewStats] as const;
      }),
    );
    setStats(Object.fromEntries(results.filter(([, v]) => !!v)));
    window.dispatchEvent(new Event('assignment-completion-changed'));
  }, [teamId, coachId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const { completion, error: completionError } = useAssignmentCompletion(teamId);


  useEffect(() => {
    const openPicker = () => { setSelectedLessons(assignments.map(a => a.lesson_id)); setShowPicker(true); };
    window.addEventListener('coach-open-lesson-picker', openPicker);
    return () => window.removeEventListener('coach-open-lesson-picker', openPicker);
  }, [assignments]);

  // Opened from the setup wizard's "Assign your first lesson" button.
  useEffect(() => {
    if (loading) return;
    if (sessionStorage.getItem('sportara-coach-open-picker') !== '1') return;
    sessionStorage.removeItem('sportara-coach-open-picker');
    setSelectedLessons(assignments.map(a => a.lesson_id));
    setShowPicker(true);
    setTimeout(() => document.querySelector('[data-tour="content-hub"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }, [loading, assignments]);

  useEffect(() => {
    const handler = () => { setEditing(null); setShowModal(true); };
    window.addEventListener('coach-open-add-content', handler);
    return () => window.removeEventListener('coach-open-add-content', handler);
  }, []);

  // Canonical lesson completion (server-side, same rules as the lesson itself).
  const lessonStats = useCallback((lessonId: string) => {
    const l = completion.units.find(x => x.unit_type === 'lesson' && x.source_id === lessonId);
    const total = l?.total ?? members.length;
    const done = l?.done ?? 0;
    return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [completion.units, members.length]);

  const contentStats = useCallback((id: string) => {
    const unit = completion.units.find(x => x.source_id === id);
    return { done: unit?.done ?? 0, total: unit?.total ?? 0, pct: unit?.pct ?? 0 };
  }, [completion.units]);

  const rows: Row[] = useMemo(() => {
    const lessonRows: Row[] = assignments.map(a => ({
      kind: 'lesson' as const,
      id: `lesson-${a.id}`,
      title: lessonTitle(a.lesson_id),
      sport,
      date: a.assigned_at,
      assignment: a,
    }));
    const contentRows: Row[] = content.map(i => ({
      kind: 'content' as const,
      id: `content-${i.id}`,
      title: i.title,
      sport: i.sport,
      date: i.created_at,
      item: i,
    }));
    return [...lessonRows, ...contentRows].sort((x, y) => (x.date < y.date ? 1 : -1));
  }, [assignments, content, lessonTitle, sport]);

  const visibleRows = rows.filter(r => tab === 'all' || (tab === 'lessons' ? r.kind === 'lesson' : r.kind === 'content'));

  const summary = useMemo(() => {
    let mediaDone = 0;
    let mediaTotal = 0;
    content.forEach(i => { const s = contentStats(i.id); mediaDone += s.done; mediaTotal += s.total; });
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recent = new Set<string>();
    Object.values(stats).forEach(s => (s?.viewers ?? []).forEach(v => {
      if (v.viewed_at && new Date(v.viewed_at).getTime() >= cutoff) recent.add(v.user_id);
    }));
    members.forEach(m => {
      if (m.last_active_date && new Date(m.last_active_date).getTime() >= cutoff) recent.add((m as any).player_user_id ?? m.id);
    });
    return {
      mediaDone,
      mediaTotal,
      mediaPct: mediaTotal ? Math.round((mediaDone / mediaTotal) * 100) : 0,
      activePlayers: recent.size,
    };
  }, [content, stats, members, contentStats]);


  // ---- actions -------------------------------------------------------------
  const assignedLessonIds = assignments.map(a => a.lesson_id);
  const assignmentLimit = assignmentUsage.limit;

  const toggleLesson = (id: string) =>
    setSelectedLessons(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const handleAssign = async () => {
    if (!user) return;
    const hasChanges = selectedLessons.length !== assignedLessonIds.length
      || selectedLessons.some(id => !assignedLessonIds.includes(id));
    if (!hasChanges) {
      toast('No changes to save');
      setShowPicker(false);
      return;
    }
    const { data, error } = await supabase.rpc('set_team_lesson_assignments' as any, {
      p_team_id: teamId,
      p_lesson_ids: selectedLessons,
    });
    if (error) {
      if (error.message.includes('6 unique active lessons')) {
        toast.info('Basic allows up to 6 unique active lessons. Upgrade for more.');
        onUpgrade?.();
      } else {
        toast.error('Failed to update assignments');
      }
      return;
    }
    if (data) setAssignmentUsage(data as unknown as AssignmentUsage);
    toast.success('Assignments updated');
    setShowPicker(false);
    fetchAll();
  };

  const removeAssignment = async (id: string) => {
    const nextIds = assignments.filter(a => a.id !== id).map(a => a.lesson_id);
    const { data, error } = await supabase.rpc('set_team_lesson_assignments' as any, {
      p_team_id: teamId,
      p_lesson_ids: nextIds,
    });
    if (error) { toast.error('Failed to remove'); return; }
    if (data) setAssignmentUsage(data as unknown as AssignmentUsage);
    setAssignments(prev => prev.filter(a => a.id !== id));
  };

  const deleteContent = async (id: string) => {
    if (!confirm('Delete this content?')) return;
    const { error } = await supabase.from('coach_content' as any).delete().eq('id', id);
    if (error) { toast.error('Failed to delete'); return; }
    toast.success('Deleted');
    setContent(prev => prev.filter(i => i.id !== id));
  };

  const scopeLabel = (item: ContentItem) => {
    if (item.assigned_to == null) return 'Whole Roster';
    const names = item.assigned_to
      .map(uid => rosterPlayers.find(p => p.user_id === uid)?.username)
      .filter(Boolean) as string[];
    if (names.length === 0) return `${item.assigned_to.length} player${item.assigned_to.length === 1 ? '' : 's'}`;
    return names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3}` : '');
  };

  const counts = {
    all: rows.length,
    lessons: assignments.length,
    media: content.length,
  };

  const TABS: { id: Tab; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'lessons', label: 'Lessons', count: counts.lessons },
    { id: 'media', label: 'Videos & Articles', count: counts.media },
  ];

  const emptyCopy =
    tab === 'lessons'
      ? 'No lessons assigned yet — use Assign Lessons to highlight content in your players\u2019 learning path'
      : tab === 'media'
        ? 'No videos or articles yet — paste a YouTube link or article URL to share with your team'
        : 'You haven\u2019t assigned anything yet — tap Add Content to get started!';

  return (
    <div className="glass-card p-5" data-tour="content-hub">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="font-display font-bold text-base flex items-center gap-2 text-foreground">
          <Layers className="w-4 h-4 text-primary" /> Content Hub
        </h3>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const next = !showPicker;
              if (next) setSelectedLessons(assignments.map(a => a.lesson_id));
              setShowPicker(next);
            }}
            className="font-display font-bold"
          >
            {showPicker ? 'Cancel' : 'Assign Lessons'}
          </Button>
          <Button
            size="sm"
            data-tour="add-content"
            onClick={() => { setEditing(null); setShowModal(true); }}
            className="font-display font-bold"
            style={{ backgroundColor: '#2563eb' }}
          >
            <Plus className="w-4 h-4 mr-1" /> Add Content
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {[
          {
            label: 'Active lessons',
            value: assignmentLimit === -1
              ? `${assignmentUsage.active_assignments}`
              : `${assignmentUsage.active_assignments}/${assignmentLimit}`,
          },
          {
            label: 'Assignments done',
            value: completionError ? '!' : completion.has_data ? `${completion.completion_pct}%` : '—',
          },
          { label: 'Media viewed', value: summary.mediaTotal ? `${summary.mediaPct}%` : '—' },

          { label: 'Active (7d)', value: `${summary.activePlayers}` },
        ].map(s => (
          <div key={s.label} className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 px-3 py-2 text-center">
            <p className="font-display font-extrabold text-lg text-foreground leading-tight">{s.value}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-white/10 mb-3 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`pb-2 -mb-px whitespace-nowrap text-xs font-display font-bold border-b-2 transition-colors ${
              tab === t.id
                ? 'text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground/80'
            }`}
            style={tab === t.id ? { borderBottomColor: '#2563eb' } : undefined}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {/* Assign lessons picker */}
      {showPicker && (
        <Card className="p-4 border-primary/30 space-y-4 mb-3">
          <CoachLessonBuilder
            coachId={coachId}
            teamId={teamId}
            sport={sport}
            selectedLessonIds={selectedLessons}
            onToggleLesson={toggleLesson}
            rosterPlayers={rosterPlayers}
          />
          <div className="pt-3 border-t border-border">
            <p className="text-[10px] text-muted-foreground font-display font-bold uppercase tracking-wider mb-2">
              Sportara curriculum
            </p>
            <LessonPicker
              sport={sport}
              selectedLessonIds={selectedLessons}
              onToggleLesson={toggleLesson}
              onAssign={handleAssign}
              assignLabel="Save"
              persistedCount={assignmentUsage.active_assignments}
              assignmentLimit={assignmentLimit}
            />
          </div>
        </Card>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" />
        </div>
      ) : visibleRows.length === 0 ? (
        <p className="text-xs text-muted-foreground font-body text-center py-8 px-4">{emptyCopy}</p>
      ) : (
        <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
          {visibleRows.map(row => {
            const isLesson = row.kind === 'lesson';
            const Icon = isLesson ? BookOpen : TYPE_ICON[row.item.type];
            const iconColor = isLesson ? 'text-blue-400' : TYPE_COLOR[row.item.type];
            const s = isLesson ? lessonStats(row.assignment.lesson_id) : contentStats(row.item.id);
            const isOpen = expanded === row.id;
            const stat = !isLesson ? stats[row.item.id] : null;

            return (
              <div key={row.id} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
                <div className="flex items-start gap-3 p-3">
                  <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0 ${iconColor}`}>
                    <Icon className="w-4 h-4" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-sm leading-tight truncate">{row.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground font-body">
                      <span className="capitalize">{row.sport}</span>
                      <span>·</span>
                      <span className="truncate">{isLesson ? 'Whole Roster' : scopeLabel(row.item)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div className={`h-full transition-all ${barColor(s.pct)}`} style={{ width: `${s.pct}%` }} />
                      </div>
                      <span className="text-[10px] font-display font-bold text-muted-foreground shrink-0">
                        {s.done}/{s.total} {isLesson ? 'completed' : 'watched'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-0.5 shrink-0">
                    {!isLesson && row.item.url && (
                      <a
                        href={row.item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-7 h-7 rounded hover:bg-white/10 flex items-center justify-center text-muted-foreground"
                        aria-label="Preview"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {!isLesson && (
                      <button
                        onClick={() => { setEditing(row.item); setShowModal(true); }}
                        className="w-7 h-7 rounded hover:bg-white/10 flex items-center justify-center text-muted-foreground"
                        aria-label="Edit"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => (isLesson ? removeAssignment(row.assignment.id) : deleteContent(row.item.id))}
                      className="w-7 h-7 rounded hover:bg-destructive/10 flex items-center justify-center text-muted-foreground hover:text-destructive"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setExpanded(isOpen ? null : row.id)}
                      aria-expanded={isOpen}
                      aria-label="Toggle details"
                      className="w-7 h-7 rounded hover:bg-white/10 flex items-center justify-center text-muted-foreground"
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {isOpen && isLesson && (
                  <div className="border-t border-white/10 bg-white/[0.02] px-3 py-2 space-y-2">
                    <LessonContentEditor
                      lessonId={row.assignment.lesson_id}
                      coachId={coachId}
                      teamId={teamId}
                      defaultSport={sport as any}
                      rosterPlayers={rosterPlayers}
                    />
                    <LessonProgressPanel lessonId={row.assignment.lesson_id} teamId={teamId} />
                  </div>
                )}

                {isOpen && !isLesson && (
                  <div className="border-t border-white/10 bg-white/[0.02] px-3 py-2 space-y-1 max-h-48 overflow-y-auto">
                    {(stat?.viewers ?? []).map(v => (
                      <div key={v.user_id} className="flex items-center gap-2 text-xs font-body text-foreground/80">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="flex-1 truncate">{v.username || 'Unknown'}</span>
                        {v.viewed_at && (
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(v.viewed_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ))}
                    {(stat?.pending ?? []).map(v => (
                      <div key={v.user_id} className="flex items-center gap-2 text-xs font-body text-foreground/60">
                        <Circle className="w-3.5 h-3.5 text-muted-foreground/40" />
                        <span className="flex-1 truncate">{v.username || 'Unknown'}</span>
                        <span className="text-[10px] text-muted-foreground">Not yet</span>
                      </div>
                    ))}
                    {!stat && <p className="text-[11px] text-muted-foreground italic">No view data yet</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AddContentModal
        open={showModal}
        onClose={() => setShowModal(false)}
        coachId={coachId}
        teamId={teamId}
        defaultSport={sport as any}
        rosterPlayers={rosterPlayers}
        editing={editing}
        onSaved={fetchAll}
      />
    </div>
  );
}

export default ContentHub;
